<template>
  <div class="sample-detail-basic-info">
    <h3>Basic Information</h3>
    <div class="form-group flex-group multiple-texts">
      <div class="date-received-group form-field flex-group">
        <label class="k-textbox-label" for="sampleNumber">Sample ID</label>
        <input
          id="sampleNumber"
          class="k-textbox"
          ref="sampleNumber"
          v-model="sampleData.sampleNumber"
          required
          type="text"
          @change="$emit('sample-changed', $event.target.value)"
          tabindex="-1"
        />
      </div>
      <div class="form-field flex-group">
        <label class="k-textbox-label" for="dateReceived">Date Received</label>
        <input id="dateReceived" class="k-textbox" v-model="sampleData.dateReceived" required type="text" tabindex="-1" />
      </div>
    </div>
    <CustomerAutoComplete
      :formData="sampleData"
      :tabindex="1"
      :addTabindex="500"
      :mapping="mappings.Customer"
      ref="customerField"
      @customer-modal-closed="handleCustomerModalClose"
    />
    <UnitAutoComplete
      @added-unit="addedUnit"
      :formData="sampleData"
      :tabindex="3"
      :addTabindex="505"
      :mapping="mappings.Unit"
      @unit-modal-closed="handleUnitModalClose"
      id="unitAutoComplete"
    />
    <div class="form-field flex-group form-group">
      <label class="k-textbox-label" for="unitType">Unit Type</label>
      <p id="unitType">
        <span v-if="sampleData.unitTypeName">{{ sampleData.unitTypeName }}</span>
        &nbsp;
      </p>
    </div>
    <div class="form-field flex-group form-group">
      <label class="k-textbox-label" for="manufacturer">Manufacturer</label>
      <p id="manufacturer">
        <span v-if="sampleData.manufacturerModelName">{{ sampleData.manufacturerModelName }}</span>
        &nbsp;
      </p>
    </div>
    <div class="form-field flex-group form-group">
      <label class="k-textbox-label" for="dateSent">Date Sent</label>
      <input id="dateSent" class="k-textbox" type="text" v-model="sampleData.dateSampled" tabindex="5" />
    </div>
    <div class="flex-group">
      <div class="form-field flex-group form-group">
        <label class="k-textbox-label" for="makeUpOil">Make Up Oil</label>
        <input id="makeUpOil" class="k-textbox" type="number" v-model.number="sampleData.makeUpOil" tabindex="6" />
      </div>
      <AutoComplete
        :formData="sampleData"
        :tabindex="-1"
        store="UnitOfMeasure"
        :mapping="mappings.MakeUpOilUnitOfMeasure"
        :allowEmpty="false"
        :removeLabelClass="true"
        placeholder
      />
    </div>
    <div class="flex-group">
      <div class="form-field flex-group form-group small-input-group">
        <label class="k-textbox-label" for="meterReading">Meter Reading</label>
        <input
          id="meterReading"
          class="k-textbox small-input"
          type="number"
          v-model.number="sampleData.meterReading"
          tabindex="7"
        />
        <AutoComplete
          :formData="sampleData"
          :tabindex="-1"
          store="UnitOfMeasure"
          :mapping="mappings.MeterReadingUnitOfMeasure"
          :allowEmpty="false"
          :removeLabelClass="true"
          class="small-input"
          placeholder
        />
        <label class="k-textbox-label inline-label" for="milesOrHoursOnSample">Miles/Hours On Sample</label>
        <input
          id="milesOrHoursOnSample"
          class="k-textbox small-input"
          type="number"
          v-model.number="sampleData.milesOrHoursOnSample"
          tabindex="8"
        />
      </div>
      <AutoComplete
        :formData="sampleData"
        :tabindex="-1"
        store="UnitOfMeasure"
        :mapping="mappings.MilesOrHoursOnSampleUnitOfMeasure"
        :allowEmpty="false"
        :removeLabelClass="true"
        class="small-input"
        placeholder
      />
    </div>
    <FluidAutoComplete
      :formData="sampleData"
      :mapping="mappings.Fluid"
      :tabindex="9"
      :addTabindex="510"
      store="Fluid"
      @added-fluid="addedFluid"
      @fluid-selected-oil="fluidSelectedOil"
      ref="fluidAutoComplete"
      :startingFluidTypeID="1"
    />
    <AutoComplete
      :formData="sampleData"
      :tabindex="10"
      store="FluidAction"
      :mapping="mappings.FluidAction"
      :allowEmpty="false"
      placeholder
      >Fluid Action</AutoComplete
    >
    <div class="flex-group form-field form-group">
      <label class="k-textbox-label" for="jobNumber">Job Number</label>
      <input id="jobNumber" class="k-textbox" type="text" v-model="sampleData.jobNumber" tabindex="11" />
    </div>
  </div>
</template>

<script>
import SampleMixin from '../../shared/SampleMixin';
import AutoComplete from '../../shared/AutoComplete.vue';
import CustomerAutoComplete from '../../customer/CustomerAutoComplete.vue';
import UnitAutoComplete from '../../unit/UnitAutoComplete.vue';
import FluidAutoComplete from '../../fluid/FluidAutoComplete.vue';
import OilSampleConstants from '../../../constants/column-name/oil-sample.column-name.constants';
import UnitConstants from '../../../constants/column-name/unit.column-name.constants';
import CustomerConstants from '../../../constants/column-name/unit.column-name.constants';
import AjaxDataService from '../../../../services/ajax.service';
export default {
  name: 'SampleDetailBasicInfo',
  props: {
    mappings: {
      type: Object,
      required: true
    },
    sampleData: {
      type: Object,
      required: true
    }
  },
  mixins: [SampleMixin],
  methods: {
    resetFocus(isPreviousSampleAndIsNextDisabled) {
      this.$refs.sampleNumber.focus();
    },
    async getObjectFromServer(mapping, lookupValue) {
      const { searchEndPoint } = this.$store.state.AutoCompletes[mapping.store];
      const getParams = {
        pageSize: 0,
        [mapping.serverIdKey]: lookupValue,
        [mapping.serverIdOperator]: 'eq'
      };
      const serviceData = await AjaxDataService.getData(searchEndPoint, getParams);
      if (serviceData?.data?.length) {
        return serviceData.data[0];
      }
      return null;
    },
    setOtherMappedProps(item, otherMappedProps) {
      if (otherMappedProps !== undefined) {
        Object.entries(otherMappedProps).forEach(([key, value]) => {
          // eslint-disable-next-line no-param-reassign
          this.sampleData[key] = item[value];
        });
      }
      return item;
    },
    async setCustomer(unit) {
      const customer = await this.getObjectFromServer(this.mappings.Customer, unit[UnitConstants.customerId.field]);
      this.sampleData[OilSampleConstants.customerId.field] = customer[CustomerConstants.customerId.field];
      this.sampleData[OilSampleConstants.customerCode.field] = customer[CustomerConstants.customerCode.field];
      this.sampleData[OilSampleConstants.customerName.field] = customer[CustomerConstants.customerName.field];
      this.sampleData.email = customer.email;
      this.sampleData.allReportEmails = customer.allReportEmails;
      this.sampleData.goodReportEmails = customer.goodReportEmails;
      this.sampleData.badReportEmails = customer.badReportEmails;
    },
    async addedUnit(newUnit) {
      const unit = await this.getObjectFromServer(this.mappings.Unit, newUnit[UnitConstants.unitId.field]);

      this.sampleData[OilSampleConstants.unitId.field] = unit[UnitConstants.unitId.field];
      this.sampleData.billingThirdParty = customer.customerThirdParty;

      // Needs to be done when a unit is created but a customer hasn't been selected yet. This will fill
      // out the customer information in the form based on the customer that's tied to the new unit.
      await this.setCustomer(unit);

      this.setOtherMappedProps(unit, this.mappings.Unit.otherMappedProps);
    },
    async addedFluid(newFluid) {
      const getParams = {
        pageSize: 0,
        FluidNameQuery: newFluid.fluidName,
        FluidNameOperator: 'eq'
      };
      const serviceData = await AjaxDataService.getData('/admin/api/fluids', getParams);
      if (serviceData?.data?.length) {
        this.$refs.fluidAutoComplete.setFluidAfterAdd(serviceData.data[0]);
      }
    },
    fluidSelectedOil(fluidID) {
      this.$emit('get-baseline-sample', fluidID);
    },
    handleCustomerModalClose() {
      document
        .getElementById('unitAutoComplete')
        .querySelector('input')
        .focus();
    },
    handleUnitModalClose() {
      document.getElementById('dateSent').focus();
    }
  },
  components: {
    AutoComplete,
    CustomerAutoComplete,
    UnitAutoComplete,
    FluidAutoComplete
  },
  created() {
    // This is a one-time watcher.
    this.unwatchSampleData = this.$watch('sampleData', data => {
      if (data) {
        if (data.oilSampleID > 0) {
          this.$refs.sampleNumber.focus();
        } else {
          this.$refs.customerField.$refs.autoComplete.$children[0].$refs.search.focus();
        }
        if (!data.dateReceived) {
          data.dateReceived = new Date().toLocaleDateString();
          this.sampleData = data;
        }
        this.unwatchSampleData();
      }
    });
  }
};
</script>
<style lang="scss" scoped>
.k-textbox {
  width: 140px;
}

:deep(.add-edit-fluid-code),
:deep(.add-edit-fluid-action-code) {
  .flex-autocomplete-field {
    flex-grow: 0 !important;

    .autocomplete-text {
      min-width: 150px;
    }
  }
}

:deep(.add-edit-unit-name) {
  padding-right: 38px;
}

.small-input-group {
  display: flex;
  align-items: center;

  .inline-label {
    margin-left: 10px;
    margin-right: 5px;
  }
}

.small-input {
  width: 75px;
  margin-left: 5px;
}
</style>
